/**
 * FeatureGate - Componente para controlar acesso a funcionalidades premium
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { featuresAPI } from '../lib/api';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Lock, Crown, Mail } from 'lucide-react';

// Context
const FeaturesContext = createContext(null);

export const useFeaturesContext = () => {
  const context = useContext(FeaturesContext);
  return context;
};

// Provider
export function FeaturesProvider({ children }) {
  const { user, token } = useAuth();
  const [features, setFeatures] = useState({
    selecao_aberta: false,
    avaliacao_desempenho: false,
    banco_questoes_disc: false
  });
  const [loading, setLoading] = useState(true);

  const fetchFeatures = useCallback(async () => {
    if (!token || user?.role !== 'COMPANY') {
      setLoading(false);
      return;
    }
    
    try {
      const response = await featuresAPI.getMyFeatures();
      setFeatures(response.data.features || {});
    } catch (error) {
      console.error('Error fetching features:', error);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  const hasFeature = (featureName) => {
    return features[featureName] === true;
  };

  return (
    <FeaturesContext.Provider value={{ features, loading, hasFeature, refetch: fetchFeatures }}>
      {children}
    </FeaturesContext.Provider>
  );
}

// Feature Gate Component
export function FeatureGate({ feature, children, fallback = null }) {
  const context = useFeaturesContext();
  const { user } = useAuth();
  
  // Admin always has access
  if (user?.role === 'ADMIN') {
    return children;
  }
  
  // If not company user, show children (other roles don't need feature checks)
  if (user?.role !== 'COMPANY') {
    return children;
  }
  
  // If context not available, show children (graceful fallback)
  if (!context) {
    return children;
  }
  
  const { hasFeature } = context;
  
  if (hasFeature(feature)) {
    return children;
  }
  
  return fallback;
}

// Blocked Feature Overlay Component
export function BlockedFeatureOverlay({ feature, title, description }) {
  const featureNames = {
    selecao_aberta: 'Seleção de Candidatos',
    avaliacao_desempenho: 'Avaliação de Desempenho',
    banco_questoes_disc: 'Banco de Questões DISC'
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] bg-slate-50 rounded-xl border-2 border-dashed border-slate-300 p-8">
      <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
        <Lock className="w-8 h-8 text-amber-600" />
      </div>
      <h3 className="text-xl font-semibold text-slate-900 mb-2">
        {title || `Funcionalidade Bloqueada`}
      </h3>
      <p className="text-slate-600 text-center max-w-md mb-6">
        {description || `O acesso à ${featureNames[feature] || 'esta funcionalidade'} não está liberado para sua conta. Entre em contato com o administrador para solicitar acesso.`}
      </p>
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Crown className="w-4 h-4 text-amber-500" />
        <span>Funcionalidade Premium</span>
      </div>
      <Button 
        variant="outline" 
        className="mt-6"
        onClick={() => window.location.href = 'mailto:contato@g2egente.com.br?subject=Solicitar%20Acesso%20Premium'}
      >
        <Mail className="w-4 h-4 mr-2" />
        Solicitar Acesso
      </Button>
    </div>
  );
}

// Hook to check feature access
export function useFeatureAccess(feature) {
  const context = useFeaturesContext();
  const { user } = useAuth();
  
  // Admin always has access
  if (user?.role === 'ADMIN') {
    return { hasAccess: true, loading: false };
  }
  
  if (!context) {
    return { hasAccess: true, loading: false };
  }
  
  return {
    hasAccess: context.hasFeature(feature),
    loading: context.loading
  };
}

export default FeatureGate;
